import {
  KNOWLEDGE_DOC_BOX_INTERACTION,
  useGeckoAnalytics,
} from 'features/analytics'
import { useChatbotAttachments } from 'features/chatbot'
import { DashboardTabInfo } from 'features/knowledgeDocumentViewer/components/ContextMenu'
import { Box } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { useDocumentTabDataContextOptional } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import {
  SelectedInstrument,
  useInstrumentSelectionOptional,
} from 'features/knowledgeDocumentViewer/contexts/InstrumentSelectionContext'
import {
  ActionMode,
  useKnowledgeDocumentUXContext,
} from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { normalizeForMatching } from 'features/knowledgeDocumentViewer/contexts/TimeSeriesLinkContext'
import { useBoxClickHandlers } from 'features/knowledgeDocumentViewer/hooks/useBoxClickHandlers'
import { TimeSeriesHistoryItem } from 'features/knowledgeDocumentViewer/types'
import { DocumentViewerAssetType } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { createTimeSeriesTab } from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import { parseViewerBoxId } from 'features/knowledgeDocumentViewer/utils/viewerOcrBoxId'
import React, { useCallback, useMemo, useState } from 'react'

import { LinkedDocumentsNavigationParams } from './useLinkedDocumentsNavigation'
import { OcrResultWithPage } from './useOcrResults'

interface UseBoxClickHandlerOptions {
  hasTimeSeriesData: (instrumentText: string | undefined) => boolean
  getMatchedDatapoints: (
    instrumentText: string | undefined
  ) => Array<{ id: string }>
  navigateToLinkedDocuments: (
    params: LinkedDocumentsNavigationParams
  ) => boolean
  setIsChatbotOpen: (open: boolean) => void
}

export function useBoxClickHandler({
  hasTimeSeriesData,
  getMatchedDatapoints,
  navigateToLinkedDocuments,
  setIsChatbotOpen,
}: UseBoxClickHandlerOptions) {
  const {
    activeTabId,
    openTabs,
    updateDocumentHistoryItem,
    actionMode,
    addTab,
    setActiveTabId,
    addSensorsToTimeSeriesTab,
    currentDocumentId,
    currentPageNumber,
    currentSelectedBoxIds,
  } = useKnowledgeDocumentUXContext()

  const { addAttachment, attachments } = useChatbotAttachments()
  const { trackEvent } = useGeckoAnalytics()

  const instrumentSelection = useInstrumentSelectionOptional()
  const documentSubjects = useDocumentTabDataContextOptional()?.documentSubjects

  const instrumentSubjectByAlias = useMemo(() => {
    const map = new Map<
      string,
      { assetId: string; subjectUid: string; name: string }
    >()
    if (!instrumentSelection) return map
    for (const subject of documentSubjects ?? []) {
      if (subject.kind !== 'instrument') continue
      for (const alias of subject.aliases) {
        for (const text of [alias.norm, alias.value]) {
          const key = normalizeForMatching(text)
          if (key && !map.has(key)) {
            map.set(key, {
              assetId: subject.asset_id,
              subjectUid: subject.subject_uid,
              name: subject.name,
            })
          }
        }
      }
    }
    return map
  }, [instrumentSelection, documentSubjects])

  const resolveInstrument = useCallback(
    (box: Box): SelectedInstrument | null => {
      const label = box.label?.trim()
      if (!label) return null
      const normalized = normalizeForMatching(label)
      if (!normalized) return null
      const subject = instrumentSubjectByAlias.get(normalized)
      const matchedDatapoints = getMatchedDatapoints(label)
      if (!subject && matchedDatapoints.length === 0) return null
      return {
        key: subject?.assetId ?? normalized,
        label: subject?.name ?? label,
        sourceLabel: label,
        datapointIds: matchedDatapoints.map((dp) => dp.id),
        assetId: subject?.assetId,
        subjectUid: subject?.subjectUid,
        documentId: currentDocumentId,
        boxIds: [box.id],
      }
    },
    [instrumentSubjectByAlias, getMatchedDatapoints, currentDocumentId]
  )

  const [boxContextMenuPosition, setBoxContextMenuPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [contextMenuBox, setContextMenuBox] = useState<Box | null>(null)

  const boxToOcrResult = useCallback(
    (box: Box): OcrResultWithPage => {
      const parsed = parseViewerBoxId(box.id)
      const pageIndex = parsed?.spreadPageIndex ?? 0
      const rawId = parsed?.blockId ?? box.id
      const numericId = Number(rawId)
      const id = Number.isNaN(numericId) ? rawId : numericId

      return {
        id,
        text: box.label ?? '',
        type: box.type,
        bounding_box: { left: 0, top: 0, width: 0, height: 0 },
        pageNumber: currentPageNumber + pageIndex,
        confirmed: box.confirmed,
      }
    },
    [currentPageNumber]
  )

  const handleBoxClickAction = useCallback(
    (box: Box, event: React.MouseEvent) => {
      const boxHasTimeSeriesData = hasTimeSeriesData(box.label)

      if (boxHasTimeSeriesData && !event.shiftKey) {
        const matchedDatapoints = getMatchedDatapoints(box.label)
        if (matchedDatapoints.length > 0) {
          trackEvent(KNOWLEDGE_DOC_BOX_INTERACTION.title, {
            interaction_type: 'box_click_dashboard',
            box_id: box.id,
            box_type: box.type,
            document_id: currentDocumentId,
            page_number: currentPageNumber,
          })

          const sensorIds = matchedDatapoints.map((dp) => dp.id)
          const existingDashboardCount = openTabs.filter((tab) =>
            tab.id.startsWith('timeseries-tab-')
          ).length
          const dashboardLabel = `Dashboard ${existingDashboardCount + 1}`
          const newTab = createTimeSeriesTab(sensorIds, dashboardLabel)
          addTab(newTab)

          const tagName = box.label ?? 'Time Series'
          const sensorIdSet = new Set(sensorIds)
          const sensorsAlreadyAttached = attachments.some(
            (a) =>
              a.type === 'dashboard' &&
              a.sensorIds?.some((id) => sensorIdSet.has(id))
          )
          if (!sensorsAlreadyAttached) {
            addAttachment({
              id: newTab.id,
              type: 'dashboard',
              name: tagName,
              displayTitle: tagName,
              sensorIds,
            })
          }

          return true
        }
      }

      const ocrResult = boxToOcrResult(box)

      trackEvent(KNOWLEDGE_DOC_BOX_INTERACTION.title, {
        interaction_type: 'box_click_linked_docs',
        box_id: box.id,
        box_type: box.type,
        document_id: currentDocumentId,
        page_number: currentPageNumber,
      })

      updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [box.id] })

      const navigated = navigateToLinkedDocuments({
        ocrResult,
        forceNewTab: true,
        showReferencesTableOnSelect: true,
      })

      return navigated
    },
    [
      activeTabId,
      addAttachment,
      addTab,
      attachments,
      boxToOcrResult,
      currentDocumentId,
      currentPageNumber,
      getMatchedDatapoints,
      hasTimeSeriesData,
      navigateToLinkedDocuments,
      openTabs,
      trackEvent,
      updateDocumentHistoryItem,
    ]
  )

  const boxClickHandlers = useBoxClickHandlers({
    selectedBoxIds: currentSelectedBoxIds,
    setSelectedBoxIds: (selectedBoxIds: string[]) => {
      updateDocumentHistoryItem(activeTabId, { selectedBoxIds })
    },
    onBoxClickAction: handleBoxClickAction,
  })

  const onBoxClick = useCallback(
    (box: Box, event: React.MouseEvent) => {
      trackEvent(KNOWLEDGE_DOC_BOX_INTERACTION.title, {
        interaction_type: 'box_click',
        box_id: box.id,
        box_type: box.type,
        action_mode: actionMode,
        document_id: currentDocumentId,
        page_number: currentPageNumber,
      })
      // Deliberately no selectedBoxIds write: selection visuals come from the
      // alias overlay, and box ids would bloat the URL-synced tab state.
      if (instrumentSelection) {
        const instrument = resolveInstrument(box)
        if (!instrument) return
        if (event.shiftKey) {
          instrumentSelection.toggle(instrument)
        } else {
          instrumentSelection.select(instrument)
        }
        return
      }
      const handler = boxClickHandlers[actionMode]
      if (handler) {
        handler(box, event)
      }
    },
    [
      actionMode,
      boxClickHandlers,
      trackEvent,
      currentDocumentId,
      currentPageNumber,
      instrumentSelection,
      resolveInstrument,
    ]
  )

  const onBoxAuxClick = useCallback(
    (box: Box, event: React.MouseEvent) => {
      if (event.button === 1 && actionMode === ActionMode.Info) {
        event.preventDefault()
        if (box.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR) {
          const ocrResult = boxToOcrResult(box)
          navigateToLinkedDocuments({ ocrResult, forceNewTab: true })
        }
      }
    },
    [actionMode, boxToOcrResult, navigateToLinkedDocuments]
  )

  const onBoxContextMenu = useCallback(
    (box: Box, event: React.MouseEvent) => {
      if (actionMode === ActionMode.Info) {
        event.preventDefault()
        const boxHasTimeSeriesData = hasTimeSeriesData(box.label)
        if (
          box.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR ||
          boxHasTimeSeriesData
        ) {
          setContextMenuBox(box)
          setBoxContextMenuPosition({ x: event.clientX, y: event.clientY })
        }
      }
    },
    [actionMode, hasTimeSeriesData]
  )

  const handleBoxContextMenuClose = useCallback(() => {
    setBoxContextMenuPosition(null)
    setContextMenuBox(null)
  }, [])

  const handleBoxContextMenuOpen = useCallback(() => {
    if (contextMenuBox) {
      const ocrResult = boxToOcrResult(contextMenuBox)
      navigateToLinkedDocuments({ ocrResult, forceNewTab: false })
    }
  }, [contextMenuBox, boxToOcrResult, navigateToLinkedDocuments])

  const handleBoxContextMenuOpenInNewTab = useCallback(() => {
    if (contextMenuBox) {
      const ocrResult = boxToOcrResult(contextMenuBox)
      navigateToLinkedDocuments({ ocrResult, forceNewTab: true })
    }
  }, [contextMenuBox, boxToOcrResult, navigateToLinkedDocuments])

  const handleBoxContextMenuAddToContext = useCallback(() => {
    if (!contextMenuBox) return

    const matchedDatapoints = getMatchedDatapoints(contextMenuBox.label)
    if (matchedDatapoints.length === 0) return

    const sensorIds = matchedDatapoints.map((dp) => dp.id)
    const tagName = contextMenuBox.label ?? 'Time Series'

    const sensorIdSet = new Set(sensorIds)
    const sensorsAlreadyAttached = attachments.some(
      (a) =>
        a.type === 'dashboard' && a.sensorIds?.some((id) => sensorIdSet.has(id))
    )

    if (!sensorsAlreadyAttached) {
      const existingDashboardCount = openTabs.filter((tab) =>
        tab.id.startsWith('timeseries-tab-')
      ).length
      const dashboardLabel = `Dashboard ${existingDashboardCount + 1}`
      const newTab = createTimeSeriesTab(sensorIds, dashboardLabel)
      addTab(newTab)

      addAttachment({
        id: newTab.id,
        type: 'dashboard',
        name: tagName,
        displayTitle: tagName,
        sensorIds,
      })
    }

    setIsChatbotOpen(true)
  }, [
    contextMenuBox,
    getMatchedDatapoints,
    attachments,
    openTabs,
    addTab,
    addAttachment,
    setIsChatbotOpen,
  ])

  const contextMenuBoxHasTimeSeriesData = useMemo(
    () => hasTimeSeriesData(contextMenuBox?.label),
    [contextMenuBox?.label, hasTimeSeriesData]
  )

  const contextMenuBoxIsOffPageConnector =
    contextMenuBox?.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR

  const existingDashboardTabs: DashboardTabInfo[] = useMemo(() => {
    return openTabs
      .filter((tab) => {
        if (!tab.id.startsWith('timeseries-tab-')) return false
        // Saved-chart tabs render from the resolved chart config and ignore
        // sensor-id seeding, so they are not valid "add sensor here" targets.
        const currentItem = tab.history[tab.currentIndex]
        return !(currentItem.type === 'timeseries' && currentItem.chartID)
      })
      .map((tab) => {
        const currentItem = tab.history[tab.currentIndex]
        const label =
          tab.customLabel ||
          (currentItem.type === 'timeseries'
            ? (currentItem as TimeSeriesHistoryItem).label
            : 'Dashboard')
        return { id: tab.id, label }
      })
  }, [openTabs])

  const handleBoxContextMenuAddToExistingDashboard = useCallback(
    (tabId: string) => {
      if (!contextMenuBox) return

      const matchedDatapoints = getMatchedDatapoints(contextMenuBox.label)
      if (matchedDatapoints.length === 0) return

      const sensorIds = matchedDatapoints.map((dp) => dp.id)
      addSensorsToTimeSeriesTab(tabId, sensorIds)
      setActiveTabId(tabId)
    },
    [
      contextMenuBox,
      getMatchedDatapoints,
      addSensorsToTimeSeriesTab,
      setActiveTabId,
    ]
  )

  const onMultipleBoxSelect = useCallback(
    (boxIds: string[], boxes?: Box[]) => {
      trackEvent(KNOWLEDGE_DOC_BOX_INTERACTION.title, {
        interaction_type: 'multi_select',
        selected_count: boxIds.length,
        action_mode: actionMode,
        document_id: currentDocumentId,
        page_number: currentPageNumber,
      })
      if (instrumentSelection) {
        const instruments = (boxes ?? [])
          .map(resolveInstrument)
          .filter(
            (instrument): instrument is SelectedInstrument => !!instrument
          )
        instrumentSelection.addMany(instruments)
        return
      }
      updateDocumentHistoryItem(activeTabId, {
        selectedBoxIds: [...currentSelectedBoxIds, ...boxIds],
      })
    },
    [
      activeTabId,
      currentDocumentId,
      currentPageNumber,
      currentSelectedBoxIds,
      updateDocumentHistoryItem,
      trackEvent,
      actionMode,
      instrumentSelection,
      resolveInstrument,
    ]
  )

  const handlePageClick = useCallback(
    (event: React.MouseEvent) => {
      const hasInstrumentSelection =
        !!instrumentSelection && instrumentSelection.selected.size > 0
      if (currentSelectedBoxIds.length === 0 && !hasInstrumentSelection) return

      const target = event.target as HTMLElement

      const shouldPreserveSelection =
        target.tagName === 'rect' ||
        target.closest('tr') !== null ||
        target.closest('button') !== null ||
        target.closest('input') !== null ||
        target.closest('[data-preserve-selection]') !== null

      if (!shouldPreserveSelection) {
        instrumentSelection?.clear()
        updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [] })
      }
    },
    [
      activeTabId,
      currentSelectedBoxIds,
      updateDocumentHistoryItem,
      instrumentSelection,
    ]
  )

  return {
    boxContextMenuPosition,
    contextMenuBox,
    onBoxClick,
    onBoxAuxClick,
    onBoxContextMenu,
    handleBoxContextMenuClose,
    handleBoxContextMenuOpen,
    handleBoxContextMenuOpenInNewTab,
    handleBoxContextMenuAddToContext,
    contextMenuBoxHasTimeSeriesData,
    contextMenuBoxIsOffPageConnector,
    existingDashboardTabs,
    handleBoxContextMenuAddToExistingDashboard,
    onMultipleBoxSelect,
    handlePageClick,
  }
}
